package com.schoolconnect.app.repository.systemlookup

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.LookupItemResponse
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

data class SystemLookupSnapshot(
    val categories: List<String> = emptyList(),
    val items: List<LookupItemResponse> = emptyList(),
    val byCategory: Map<String, List<LookupItemResponse>> = emptyMap()
) {
    fun values(category: String): List<LookupItemResponse> =
        byCategory[category.trim()] ?: emptyList()

    fun activeValues(category: String): List<LookupItemResponse> =
        values(category).filter { it.isActive != false }

    fun value(category: String, code: String): LookupItemResponse? =
        values(category).firstOrNull { it.code.equals(code.trim(), ignoreCase = true) }
}

/** Generic, role-independent system metadata loaded once during app startup. */
object SystemLookupRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SystemLookupRepository"

    private val _snapshot = MutableStateFlow(SystemLookupSnapshot())
    val snapshot: StateFlow<SystemLookupSnapshot> = _snapshot.asStateFlow()

    suspend fun refresh(forceRemote: Boolean = true): Result<SystemLookupSnapshot> {
        if (!forceRemote) {
            val cached = _snapshot.value
            val cacheIsComplete = cached.items.isNotEmpty() &&
                cached.categories.isNotEmpty() &&
                cached.categories.all { cached.values(it).isNotEmpty() }
            if (cacheIsComplete) {
                scLog("$TAG refresh cacheHit categories=${cached.categories.size} items=${cached.items.size}")
                return Result.success(cached)
            }
        }

        scLog("$TAG refresh start")
        return try {
            val bulkItems = KtorApiClient.getSystemLookups().getOrElse { emptyList() }
            val catalogCategories = KtorApiClient.getSystemLookupCategories().getOrElse { emptyList() }
            val normalizedBulkCategories = bulkItems
                .mapNotNull { it.category?.trim()?.takeIf(String::isNotBlank) }
                .map(String::uppercase)
                .toSet()
            // The aggregate endpoint may be paginated/truncated even when it
            // reports a larger count. Recover missing categories through the
            // documented category endpoint, especially SCHOOL_BOARD,
            // SCHOOL_TYPE, and SCHOOL_MEDIUM used by Add School.
            val missingCategoryItems = catalogCategories
                .map(String::trim)
                .filter(String::isNotBlank)
                .filterNot { it.uppercase() in normalizedBulkCategories }
                .flatMap { category ->
                    KtorApiClient.getSystemLookupsByCategory(category).getOrNull()?.items.orEmpty()
                }
            val items = (bulkItems + missingCategoryItems).ifEmpty {
                catalogCategories.flatMap { category ->
                    KtorApiClient.getSystemLookupsByCategory(category).getOrNull()?.items.orEmpty()
                }
            }
            val normalizedItems = items
                .sortedWith(compareBy({ it.category.orEmpty() }, { it.sortOrder ?: 0 }, { it.code.orEmpty() }))
            // Keep the category catalog even if a category temporarily has no
            // active rows. The values endpoint remains the source of detail.
            val categoriesFromItems = normalizedItems
                .mapNotNull { it.category?.trim()?.takeIf(String::isNotBlank) }
                .distinct()
            val categories = KtorApiClient.getSystemLookupCategories()
                .getOrElse { emptyList() }
                .map(String::trim)
                .filter(String::isNotBlank)
                .plus(categoriesFromItems)
                .distinct()
                .sorted()
            val byCategory = normalizedItems
                .groupBy { it.category.orEmpty() }
                .filterKeys { it.isNotBlank() }
            val snapshot = SystemLookupSnapshot(
                categories = categories,
                items = normalizedItems,
                byCategory = byCategory
            )
            _snapshot.value = snapshot
            scLog("$TAG refresh complete categories=${snapshot.categories.size} items=${snapshot.items.size}")
            Result.success(snapshot)
        } catch (throwable: Throwable) {
            scLog("$TAG refresh failure", throwable)
            Result.failure(throwable)
        }
    }

    fun values(category: String): List<LookupItemResponse> = _snapshot.value.values(category)

    fun activeValues(category: String): List<LookupItemResponse> = _snapshot.value.activeValues(category)

    fun value(category: String, code: String): LookupItemResponse? = _snapshot.value.value(category, code)

    suspend fun loadCategoryItems(category: String): Result<List<LookupItemResponse>> =
        KtorApiClient.getSystemLookupsByCategory(category).map { response ->
            val activeItems = response.items.orEmpty().filter { it.isActive != false }
            val normalizedCategory = category.trim()
            if (normalizedCategory.isNotBlank() && activeItems.isNotEmpty()) {
                val mergedItems = _snapshot.value.items
                    .filterNot { it.category.equals(normalizedCategory, ignoreCase = true) } + activeItems
                val normalizedItems = mergedItems.sortedWith(compareBy({ it.category.orEmpty() }, { it.sortOrder ?: 0 }, { it.code.orEmpty() }))
                _snapshot.value = _snapshot.value.copy(
                    categories = (_snapshot.value.categories + normalizedCategory).distinct().sorted(),
                    items = normalizedItems,
                    byCategory = normalizedItems.groupBy { it.category.orEmpty() }.filterKeys { it.isNotBlank() }
                )
            }
            activeItems
        }

    fun clear() {
        _snapshot.value = SystemLookupSnapshot()
        scLog("$TAG clear")
    }
}
